import { useState } from "react";
import { Star, X } from "lucide-react";
import { useCreateReview } from "../../hooks/services/useReviews";
import { StarsReview } from "../StarsReview";
import { usePendingReview, type PendingOrderForReview } from "../../state/PendingReviewContext";

const MIN_COMMENT_LENGTH = 10;
const MAX_COMMENT_LENGTH = 500;

interface Props {
	order: PendingOrderForReview;
}

export function ModalPendingReview({ order }: Props) {
	const { dismiss, markReviewed } = usePendingReview();
	const { create, loading, error } = useCreateReview();

	const [rating, setRating] = useState(0);
	const [comment, setComment] = useState("");
	const [submitted, setSubmitted] = useState(false);

	const commentTrimmed = comment.trim();
	const commentLength = commentTrimmed.length;
	const hasComment = commentLength > 0;
	const commentTooShort = hasComment && commentLength < MIN_COMMENT_LENGTH;

	const canSubmit = rating > 0 && !loading && !commentTooShort;

	const handleSubmit = async () => {
		if (!canSubmit) return;
		const result = await create({
			order_id: order.orderId,
			qualifier_id: order.qualifierId,
			qualified_id: order.providerId,
			punctuation: rating,
			comment_review: commentTrimmed,
		});
		if (result) {
			setSubmitted(true);
			setTimeout(() => markReviewed(order.orderId), 1800);
		}
	};

	return (
		<div
			className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4'
			onClick={dismiss}
		>
			<div
				className='relative w-full max-w-sm rounded-2xl bg-white px-6 py-5 soft-border card-shadow'
				onClick={(e) => e.stopPropagation()}
			>
				{/* X button */}
				<button
					type='button'
					onClick={dismiss}
					aria-label='Cerrar'
					className='absolute right-4 top-4 rounded-full p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 cursor-pointer'
				>
					<X className='h-4 w-4' />
				</button>

				{submitted ? (
					/* ── Estado de éxito ── */
					<div className='flex flex-col items-center gap-3 py-4 text-center'>
						<div className='relative'>
							<span className='absolute inset-0 rounded-full bg-primary/20 animate-ping' />
							<span className='relative inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary'>
								<Star className='h-5 w-5' fill='currentColor' />
							</span>
						</div>
						<h2 className='text-base font-semibold text-slate-900'>
							¡Gracias por tu reseña!
						</h2>
						<p className='text-xs text-slate-500'>
							Tu opinión ayuda a mejorar la comunidad Servix.
						</p>
					</div>
				) : (
					<>
						{/* ── Header ── */}
						<div className='flex items-center gap-3 pr-6'>
							<span className='inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
								<Star className='h-5 w-5' />
							</span>
							<div>
								<h2 className='text-base font-semibold text-slate-900'>
									¿Cómo fue tu experiencia?
								</h2>
								<p className='mt-0.5 text-xs text-slate-500'>
									Califica al proveedor de tu orden
								</p>
							</div>
						</div>

						{/* ── Info de la orden ── */}
						<div className='mt-4 rounded-xl bg-secondary px-4 py-3'>
							<p className='text-xs text-slate-500'>
								Orden:{" "}
								<span className='font-semibold text-slate-700'>
									{order.orderCode}
								</span>
							</p>
							<p className='mt-0.5 text-xs text-slate-500'>
								Proveedor:{" "}
								<span className='font-semibold text-primary'>
									{order.providerName}
								</span>
							</p>
						</div>

						{/* ── Estrellas ── */}
						<div className='mt-5'>
							<StarsReview value={rating} onChange={setRating} />
							{rating === 0 && (
								<p className='mt-1.5 text-center text-xs text-slate-400'>
									Selecciona una puntuación
								</p>
							)}
						</div>

						{/* ── Comentario ── */}
						<div className='mt-4'>
							<textarea
								className='w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none transition'
								rows={3}
								placeholder='Comparte tu experiencia (opcional, mín. 10 caracteres)...'
								maxLength={MAX_COMMENT_LENGTH}
								value={comment}
								onChange={(e) => setComment(e.target.value)}
							/>
							<div className='mt-1 flex items-center justify-between px-0.5'>
								{/* Validación mínimo */}
								{commentTooShort ? (
									<p className='text-xs font-medium text-red-500'>
										Mínimo {MIN_COMMENT_LENGTH} caracteres
									</p>
								) : (
									<span />
								)}
								{/* Contador */}
								<p
									className={`text-xs tabular-nums ${
										commentLength > MAX_COMMENT_LENGTH - 50
											? "text-amber-500"
											: "text-slate-400"
									}`}
								>
									{commentLength}/{MAX_COMMENT_LENGTH}
								</p>
							</div>
						</div>

						{/* ── Error de API ── */}
						{error && (
							<p className='mt-2 text-center text-xs text-red-500'>
								{typeof error === "string"
									? error
									: "No se pudo enviar la reseña."}
							</p>
						)}

						{/* ── Botones ── */}
						<div className='mt-5 flex items-center justify-between gap-3'>
							<button
								type='button'
								onClick={dismiss}
								className='flex-1 rounded-full border border-primary px-4 py-2 text-xs font-semibold text-primary transition hover:bg-primary/10 cursor-pointer'
							>
								Ahora no
							</button>
							<button
								type='button'
								onClick={handleSubmit}
								disabled={!canSubmit}
								className='flex-1 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white transition hover:bg-primary-hover cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
							>
								{loading ? "Enviando..." : "Calificar"}
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
